import * as React from "react";
import { Button, Checkbox, Input, Modal, useAlert, useToast } from "uxp/components";
import { IContextProvider } from "../../../uxp";
import { IUIPropertyDefinition, IWDDesignModeProps, PropertyPath } from '../common';
import { Property, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle } from "./common";
import { FAIcon as FontAwesomeIcon } from '../components/FAIcon';
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ContextDataAutoComplete } from "../ContextDataAutoComplete";
import { IContextData } from "../../../hooks/useContextData";
import { dataSourceLabel, deriveJSONPath, findDataSource, IDataSource, IDataSourceBinding, IModelActionDataSource, processSource, registerDataSource, TargetDataStructure } from "../data-source-picker/datasource";
import { bindContainerId } from "../../../util";
import classNames from "classnames";
import { SourcePickerWrapper } from "../data-source-picker/SourcePickerWrapper";
import './DataSourceProperty.scss';
import { $L } from '../../../localize';

interface IDataSourcePropertyEditorProps {
    title: string;
    binding: IDataSourceBinding;
    sources: IDataSource[];
    target: TargetDataStructure;
    onChange: (binding: IDataSourceBinding) => void;
    onChangeAny: (path: PropertyPath, value: any) => void;
    context: IContextProvider;
    contextData: IContextData;
    className?: string;
    designer: IWDDesignModeProps;
    containerId?: string,

    // some props to support multiple source editor
    hideLabels?: boolean
    hideAdvancedOptions?: boolean
    buttonContainerClassName?: string
    renderButton?: () => JSX.Element

    // custom trigger for a BOUND source (replaces the pill + wrench block only;
    // the picker + advanced modal still work through the callbacks)
    renderTrigger?: (api: { ds: IDataSource, name: string, openPicker: () => void, openAdvanced: () => void }) => JSX.Element
}

export const DataSourcePropertyEditor: React.FunctionComponent<IDataSourcePropertyEditorProps> = (props) => {
    const { title, binding, sources, target, onChange, onChangeAny, context, contextData, className } = props
    const alerts = useAlert();
    const toast = useToast()

    let ds = findDataSource(binding?.source, sources);
    let isBound = !!ds;
    let name = ds ? dataSourceLabel(ds) : $L('widgetdesigner.datasource.bind-a-source', 'Bind a source');

    const [isSelecting, setIsSelecting] = React.useState(false);
    const [showAdvanced, setShowAdvanced] = React.useState(false)

    function onSelectSource(source: IDataSource) {
        let existing = findDataSource(source.id, sources);

        let newSources = (sources || []).slice();
        if (existing) {
            let index = newSources.findIndex(s => s.id === source.id)
            if (index != -1) newSources.splice(index, 1, source)
        }
        else {
            source = registerDataSource(source);
            newSources.push(source);
        }

        onChangeAny('datasources', newSources)

        let path = '';
        if (target && source.schema) {
            try {
                path = deriveJSONPath(source.schema, {}, target)
            } catch {

            }
        }
        onChange({ source: source.id, path });
        setIsSelecting(false);
    }

    function onRemoveSource(id: string) {
        const updated = [...sources].filter(s => s.id != id)
        onChangeAny('datasources', updated)
    }

    function onCloseBrowser() {
        setIsSelecting(false)
    }

    function renderSource() {
        if (!isBound) {
            return <div
                className={classNames("datasource-button", props.buttonContainerClassName)}
                onClick={() => setIsSelecting(true)}
            >
                {props.renderButton ? props.renderButton() : $L('widgetdesigner.datasource.bind-a-source', 'Bind a source')}
            </div>
        }

        let icon: IconProp = ['fas', 'sitemap']
        if (ds?.type == 'model-collection') icon = ['fas', 'database']
        else if (ds?.type == 'custom-json') icon = ['fas', 'brackets-curly']
        else if (ds?.type == 'query') icon = ['fas', 'project-diagram']
        return <div
            className={classNames("datasource-button bound", ds?.type)}
            onClick={() => setIsSelecting(true)}
        >
            <div className="icon">
                <FontAwesomeIcon icon={icon} />
            </div>

            <div className="name">
                {
                    (ds?.type == 'custom-json' || ds?.type == 'query')
                        ? <> {ds?.name}</>
                        : <>
                            <div className="model">{ds?.model}</div>
                            <div className="action">{ds?.action}</div>
                        </>
                }
            </div>
        </div>
    }

    function updateParameters(id: string, value: string) {
        const dataSources = [...sources]
        const index = dataSources.findIndex(d => d.id === ds.id)
        if (index == -1) return

        // update the params 
        let _ds = { ...ds }

        for (const p of _ds.parameters) {
            if (p.id === id) {
                p.value = value
            }
        }

        dataSources[index] = _ds

        onChangeAny('datasources', dataSources)
    }

    function updateDatasourceProps(key: string, value: any) {
        const datasources = [...sources]
        const updated = datasources.map((d: any) => {
            if (d.id === ds.id) {
                d[key] = value
            }
            return d as IDataSource
        })

        onChangeAny('datasources', updated)
    }

    return <PropertyContainer>
        <PropertyGroup>
            <Property id={bindContainerId('source', props.containerId)} >
                {!props.hideLabels && <PropertyTitle>{$L('widgetdesigner.datasource.source', 'Source')}</PropertyTitle>}
                {(props.renderTrigger && isBound)
                    ? props.renderTrigger({
                        ds,
                        name,
                        openPicker: () => setIsSelecting(true),
                        openAdvanced: () => setShowAdvanced(true),
                    })
                    : <div className="uxp_wd-data-source-editor">
                        {renderSource()}

                        {!props.hideAdvancedOptions &&
                            <div className="uxp_wd-data-source-editor__advanced-btn" onClick={() => { setShowAdvanced(true) }}>
                                <FontAwesomeIcon icon={['fas', 'tools']} />
                            </div>
                        }
                    </div>
                }
            </Property>
        </PropertyGroup>

        {/* advanced property editor */}
        <Modal
            show={showAdvanced}
            onClose={() => setShowAdvanced(false)}
            title={`Advanced configurations for the data source : ${name}`}
            className='uxp_wd-data-source-editor__advanced-modal'
            closeButton={<Button title={$L('widgetdesigner.common.done', 'Done')} onClick={() => { }} className="uxp_wd-data-source-editor__advanced-modal-close-btn" />}
        >
            <PropertyContainer>

                <PropertyGroup>
                    <PropertyGroupTitle>{$L('widgetdesigner.datasource.details', 'Details')}</PropertyGroupTitle>

                    <Property>
                        <PropertyTitle>{$L('widgetdesigner.datasource.source-id', 'Data Source ID')}</PropertyTitle>
                        <Input
                            value={(ds?.id || '')}
                            onChange={() => { }}
                            readOnly={true}
                            className="widget-designer-input"
                        />
                    </Property>

                    <Property>
                        <PropertyTitle>{$L('widgetdesigner.datasource.binding', 'Binding')}</PropertyTitle>
                        <Input
                            value={(binding?.path || '')}
                            onChange={s => onChange(Object.assign({}, binding, { path: s }))}
                            className="widget-designer-input"
                        />
                    </Property>

                </PropertyGroup>

                <PropertyGroup>
                    <PropertyGroupTitle>{$L('widgetdesigner.datasource.caching', 'Caching')}</PropertyGroupTitle>

                    <Property>
                        <Checkbox
                            checked={ds?.enableCache || false}
                            onChange={s => {
                                updateDatasourceProps('enableCache', s)
                            }}
                            label={$L('widgetdesigner.datasource.enable-caching', 'Enable caching')}
                        />
                    </Property>

                    {ds?.enableCache &&
                        <Property>
                            <PropertyTitle>{$L('widgetdesigner.datasource.cache-interval', 'Cache refresh interval(in minutes)')}</PropertyTitle>
                            <Input
                                type="number"
                                value={(ds?.cacheRefreshInterval || 0).toString()}
                                onChange={s => {
                                    let num = Number(s)
                                    if (num < 0) num = 0
                                    if (num > 10000) num = 10000
                                    updateDatasourceProps('cacheRefreshInterval', num)
                                }}
                            />
                        </Property>
                    }
                </PropertyGroup>

                {ds?.parameters?.length > 0 && <PropertyGroup>
                    <PropertyGroupTitle>{$L('widgetdesigner.datasource.parameters', 'Parameters')}</PropertyGroupTitle>

                    {ds?.parameters?.map((p: any) => {
                        return <Property>
                            <PropertyTitle>{p.description}</PropertyTitle>
                            <ContextDataAutoComplete
                                value={p.value || ''}
                                onChange={s => updateParameters(p.id, s)}
                                contextData={contextData}
                            />
                        </Property>
                    })}
                </PropertyGroup>}

            </PropertyContainer>
        </Modal>

        <SourcePickerWrapper
            designer={props.designer}
            uxpContext={context}
            selectedSource={ds}
            existingSources={sources}
            onSelect={onSelectSource}
            onRemove={onRemoveSource}
            contextData={contextData}
            onClose={onCloseBrowser}
            target={target}
            show={isSelecting}
        />

    </PropertyContainer>

}

export function DataSourceProperty(title: string, source: IDataSourceBinding, sources: IDataSource[], context: IContextProvider, designer: IWDDesignModeProps, target: TargetDataStructure): IUIPropertyDefinition<IDataSourceBinding> {
    return {
        defaults: source,
        render: (props, uxpContext, contextData, containerId) => {

            let _source = props?.uiProps || source
            let _sources = props?.parentUIProps?.datasources || sources

            return <DataSourcePropertyEditor
                title={title}
                binding={_source}
                sources={_sources}
                target={target}
                onChange={props.onChange}
                onChangeAny={props.onChangeAny}
                context={uxpContext}
                contextData={contextData}
                designer={designer}
                containerId={containerId}
            />
        }
    };
}